import { Component, computed, OnDestroy, OnInit, signal, WritableSignal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CurrencyPipe } from '@angular/common';
import { OffcanvasService } from '../../../../_services/offcanvas-service/offcanvas.service';
import { SubscriptionApiService } from '../../../../_services/subscription-service/api/subscription-api-service.service';
import {
  PaymentMethod,
  SubscriptionDetail,
  SubscriptionFrequency,
  SubscriptionPayload,
} from '../../../../_interfaces/subscription.interface';
import { SubscriptionService } from '../../../../_services/subscription-service/subscription.service';
import { Subscription } from 'rxjs';
import { OffcanvasHeaderBtnComponent } from '../../../../shared/components/offcanvas/offcanvas-header-btn/offcanvas-header-btn.component';
import { OffcanvasBaseComponent } from '../../../../shared/components/offcanvas/offcanvas-base/offcanvas-base.component';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { PaymentMethodComponent } from '../components/payment-method/payment-method.component';
import { EmptyIndicatorPipe } from '../../../../shared/pipes/empty-indicator.pipe';

@Component({
  selector: 'app-upgrade-plan',
  imports: [
    CurrencyPipe,
    OffcanvasHeaderBtnComponent,
    OffcanvasBaseComponent,
    LoaderComponent,
    PaymentMethodComponent,
    EmptyIndicatorPipe,
  ],
  templateUrl: './upgrade-plan.component.html',
})
export class UpgradePlanComponent implements OnInit, OnDestroy {
  readonly router = inject(Router);
  readonly offcanvasService = inject(OffcanvasService);
  readonly subscriptionApiService = inject(SubscriptionApiService);
  readonly subscriptionService = inject(SubscriptionService);

  protected billingFrequency = signal<SubscriptionFrequency>(SubscriptionFrequency.MONTHLY);
  protected returnUrl: WritableSignal<string> = signal('/your-plan');
  protected availableUpgrade?: SubscriptionDetail;
  protected proRataMonthly?: string;
  protected proRataYearly?: string;
  protected packageId!: number;
  protected packageName!: string;
  protected savings!: number;
  currentYearly = signal<boolean>(false);
  isYearly = computed(() => this.billingFrequency() === SubscriptionFrequency.YEARLY);
  readonly subscriptions = new Subscription();
  protected payfastToken = signal<string>('');
  protected paymentMethod = signal<PaymentMethod>({} as PaymentMethod);

  constructor() {
    if (this.router.url.includes('accounting')) {
      this.returnUrl.set('/accounting/integrations');
    }
    if (this.router.url.includes('subscription-cards')) {
      this.returnUrl.set('/cards/subscription-cards');
    }
  }

  ngOnInit(): void {
    this.getCurrentSubscriptionDetails();
    this.getUpgradeSubscriptionDetails();
  }

  getUpgradeSubscriptionDetails() {
    this.subscriptions.add(
      this.subscriptionApiService.getUpgradeSubscription().subscribe({
        next: (res) => {
          if (res) {
            this.availableUpgrade = res[0]; // ensure only first upgrade gets shown as FE not designed to handle multiple items yet
            this.packageId = res[0]?.id;
            this.packageName = res[0]?.name;
            this.savings = this.subscriptionService.calculateSavings(res[0]?.base_cost, res[0]?.yearly_cost);
            this.getProRataAmount();
          }
        },
      }),
    );
  }
  getCurrentSubscriptionDetails() {
    this.subscriptions.add(
      this.subscriptionApiService.getCurrentSubscription().subscribe({
        next: (res) => {
          this.billingFrequency.set(res.frequency);
          this.payfastToken.set(res.payfast_token);
          this.paymentMethod.set(res.payment_method);
          this.currentYearly.set(res.frequency === SubscriptionFrequency.YEARLY);
        },
      }),
    );
  }

  getProRataAmount() {
    if (!this.proRataYearly || !this.proRataMonthly) {
      this.subscriptions.add(
        this.subscriptionService.getProRataAmount(this.packageId, this.billingFrequency()).subscribe({
          next: (res) => {
            if (this.isYearly()) {
              this.proRataYearly = res.pro_rata;
            } else {
              this.proRataMonthly = res.pro_rata;
            }
          },
        }),
      );
    }
  }

  helpDesk() {
    this.router.navigate(['/help-desk']);
    this.offcanvasService.close();
  }

  onUpgradePackage() {
    const frequency = this.billingFrequency();
    const data: SubscriptionPayload = {
      package: this.packageId,
      frequency: frequency,
    };
    this.subscriptionService.onUpgradePackage(data, this.packageName);
  }

  handleFrequencyChange(event: SubscriptionFrequency) {
    this.billingFrequency.set(event);
    this.getProRataAmount();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
